import { useContext, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Button,
  Text,
  Flex,
  Alert,
  AlertIcon,
  useToast,
  Divider,
} from '@chakra-ui/react';
import { useBilling } from '../../../store/client/BillingContext';
import { HttpContext } from '../../../context/HttpContext';
import { MeContext } from '../../../context/MeContext';
import CustomToast from '../../../common/CustomToast';
import { useStripe } from '@stripe/react-stripe-js';

const GoLiveModal = ({ isOpen, onClose }) => {
  const stripe = useStripe();
  const { authAxios } = useContext(HttpContext);
  const { fetchData: refreshMeContext } = useContext(MeContext);
  const { subscription, updateSubscription } = useBilling();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();

  const currentInterval = subscription?.plan?.interval || 'month';
  const priceId =
    subscription?.plan?.prices?.find((p) => p.interval === currentInterval)
      ?.id || subscription?.plan?.prices?.[0]?.id;

  const handleUpdatePlan = async () => {
    setLoading(true);
    setError(null);

    if (!priceId) {
      setError('Missing required price ID');
      setLoading(false);
      return;
    }

    try {
      const apiUrl = `/internal/v1/billing/subscriptions/prepare_upgrade`;
      const confirmationUrl = `/internal/v1/billing/subscriptions/confirm_upgrade`;
      const apiData = {
        price_id: priceId,
      };

      let response;
      try {
        response = await authAxios.post(apiUrl, apiData);
      } catch (apiError) {
        setError(apiError.response.data.errors[0]);
        setLoading(false);
        return;
      }

      const {
        data: {
          requires_action: requiresAction,
          client_secret: clientSecret,
          payment_method_id: paymentMethodId,
          stripe_subscription_id: stripeSubscriptionId,
        },
      } = response;

      if (requiresAction) {
        try {
          const result = await stripe.confirmPayment({
            clientSecret,
            confirmParams: {
              payment_method: paymentMethodId,
              return_url: window.location.href,
            },
            redirect: 'if_required',
          });
          response = await authAxios.post(confirmationUrl, {
            success: !result.error,
            stripe_subscription_id: stripeSubscriptionId,
            error: result.error,
          });
        } catch (error) {
          setError(error.response.data.errors[0]);
          setLoading(false);
          return;
        }
      } else {
        try {
          response = await authAxios.post(confirmationUrl, {
            success: true,
            stripe_subscription_id: stripeSubscriptionId,
          });
        } catch (error) {
          setError(error.response.data.errors[0]);
          setLoading(false);
          return;
        }
      }

      const { subscription: updatedSubscription } = response.data;

      if (updatedSubscription) {
        updateSubscription(updatedSubscription);
        refreshMeContext();
      }

      toast({
        render: (props) => (
          <CustomToast
            status="success"
            title="Success"
            description="Subscription updated successfully"
            onClose={props.onClose}
            id={props.id}
          />
        ),
      });

      onClose();
    } catch (error) {
      const errorMessage = error.response?.data?.errors[0];

      if (errorMessage) {
        toast({
          render: (props) => (
            <CustomToast
              status="error"
              title="Error"
              description={errorMessage}
              onClose={props.onClose}
              id={props.id}
            />
          ),
        });
      } else {
        toast({
          render: (props) => (
            <CustomToast
              status="error"
              title="Something went wrong"
              description="Please try again later"
              onClose={props.onClose}
              id={props.id}
            />
          ),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent py={6} px={8}>
        <ModalCloseButton />
        <Text textStyle="heading3" mt="10px" textAlign="center">
          Go live now?
        </Text>
        <Divider border="1px solid lightGrey" mt="10px" />
        <ModalBody my={4}>
          <Flex direction="column" w="100%">
            <Text textStyle="bodyFamilyRegular" fontSize="16px" mt="5 px">
              Going live will <b>immediately end your trial</b>. All trial
              passes will expire, and you can start issuing live passes right
              away.{' '}
            </Text>
            <Text textStyle="bodyFamilyRegular" fontSize="16px" mt="15px">
              You’ll be charged today and your billing cycle will start from
              today onwards.
            </Text>
            {error && (
              <Alert status="error" borderRadius="5px" mt="20px">
                <AlertIcon />
                <Text>{error}</Text>
              </Alert>
            )}
            <Flex
              w="100%"
              direction="column"
              justifyContent="center"
              alignItems="center"
              gap={4}
              mt="25px"
            >
              <Button
                variant="gradient"
                w="100%"
                onClick={handleUpdatePlan}
                isLoading={loading}
                loadingText="Processing"
              >
                Go live
              </Button>
              <Button
                variant="link"
                data-testid="confirm-cancel-trial-button"
                onClick={onClose}
              >
                <Text textStyle="bodySmall">Cancel</Text>
              </Button>
            </Flex>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default GoLiveModal;
